import { useState, type CSSProperties } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useApiQuery } from '../use-api.js';
import { MetricCard } from '../components/MetricCard.js';
import { ConceptHelpIcon } from '../components/ConceptHelpIcon.js';
import { SCORECARD_DISPLAY_NAME, SCORECARD_METRIC_HELP, metricFormat } from '../lib/scorecard-format.js';

interface PrSummary {
  pr: number;
  ts: string;
  verdict_state: string;
  verdict_reason: string;
  cost_usd?: number;
  duration_ms?: number;
  title?: string;
  author?: string;
  branch?: string;
}

interface PrsResponse {
  prs: PrSummary[];
  next_cursor: string | null;
}

interface ScorecardMetric {
  metric: string;
  baseline: number | null;
  current: number | null;
  delta: number | null;
  color: 'green' | 'yellow' | 'red' | null;
}
interface ScorecardResponse {
  org: string;
  repo: string;
  metrics: ScorecardMetric[];
}

// Per-repo dashboard surfaces only the key signals (volume / KPI / safety / speed)
// so the tile bar fits a single row and doesn't push the PR table below the fold.
// Order is intentional: volume → primary KPI → safety → speed.
const KEY_METRICS = [
  'prs_merged',
  'auto_approval_rate',
  'revert_rate',
  'ttfr_avg_seconds',
] as const;

const inputStyle: CSSProperties = {
  padding: '8px 12px',
  border: '1px solid var(--loop-neutral-300)',
  borderRadius: 'var(--loop-radius-sm)',
  fontSize: '0.875rem',
  color: 'var(--loop-neutral-1100)',
  background: 'var(--loop-neutral-0)'
};

function fmtCost(n: number | undefined): string {
  if (n === undefined || n === null) return '—';
  return `$${n.toFixed(2)}`;
}

function fmtDuration(ms: number | undefined): string {
  if (ms === undefined || ms === null) return '—';
  return `${(ms / 1000).toFixed(1)}s`;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function RepoDashboard() {
  const { org, repo } = useParams<{ org: string; repo: string }>();
  const [verdictFilter, setVerdictFilter] = useState<string>('');
  const [cursor, setCursor] = useState<string | null>(null);
  const [accumulated, setAccumulated] = useState<PrSummary[]>([]);

  // useParams types these as string|undefined. Disable the query when missing
  // so we don't fire `/api/repos/undefined/undefined/prs`. Hooks must be
  // called unconditionally — guard goes after them, before the JSX tree.
  const ready = !!(org && repo);
  const path = ready
    ? `/api/repos/${org}/${repo}/prs?limit=25${verdictFilter ? `&verdict=${verdictFilter}` : ''}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`
    : '';
  const { data, isLoading, error } = useApiQuery<PrsResponse>(
    ['repo-prs', org ?? '', repo ?? '', verdictFilter, cursor ?? ''],
    path,
    { enabled: ready }
  );

  const scorecard = useApiQuery<ScorecardResponse>(
    ['repo-scorecard', org ?? '', repo ?? ''],
    ready ? `/api/repos/${org}/${repo}/scorecard` : '',
    { enabled: ready }
  );

  // Cross-page dedupe: if two artifacts for the same PR straddle a Scan page
  // boundary, the same PR could appear twice across pages and collide on
  // React's `key={p.pr}`. Merge by PR number, keeping the newer ts.
  const mergedById = new Map<number, PrSummary>();
  const sources = cursor === null ? (data?.prs ?? []) : [...accumulated, ...(data?.prs ?? [])];
  for (const p of sources) {
    const existing = mergedById.get(p.pr);
    if (!existing || existing.ts < p.ts) mergedById.set(p.pr, p);
  }
  const allRows = [...mergedById.values()].sort((a, b) => b.ts.localeCompare(a.ts));

  function loadMore() {
    if (data?.next_cursor) {
      setAccumulated(allRows);
      setCursor(data.next_cursor);
    }
  }

  function changeVerdict(v: string) {
    setVerdictFilter(v);
    setCursor(null);
    setAccumulated([]);
  }

  if (!ready) {
    return <p className="error">Missing org/repo in route.</p>;
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--loop-space-100)', marginBottom: 4 }}>
        <h2 style={{ fontSize: '1.125rem', fontWeight: 664, margin: 0, color: 'var(--loop-neutral-1100)' }}>
          {org} / {repo}
        </h2>
        <ConceptHelpIcon />
      </div>
      <p style={{ marginBottom: 24, color: 'var(--loop-neutral-700)', fontSize: '0.875rem' }}>Per-repo dashboard</p>

      <div className="card">
        <h3 style={{ marginBottom: 12, fontSize: '0.875rem', fontWeight: 664, color: 'var(--loop-neutral-900)' }}>Metrics</h3>
        {scorecard.isLoading && <p style={{ color: 'var(--loop-neutral-700)', fontSize: '0.875rem' }}>Loading…</p>}
        {scorecard.error && <p className="error">Error: {scorecard.error instanceof Error ? scorecard.error.message : String(scorecard.error)}</p>}
        {scorecard.data && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${KEY_METRICS.length}, minmax(0, 1fr))`,
              gap: 12,
              alignItems: 'stretch',
            }}
          >
            {KEY_METRICS.map(key => {
              const m = scorecard.data.metrics.find(x => x.metric === key);
              if (!m) return null;
              return (
                <MetricCard
                  key={m.metric}
                  name={SCORECARD_DISPLAY_NAME[m.metric] ?? m.metric}
                  baseline={m.baseline}
                  current={m.current}
                  delta={m.delta}
                  status={m.color ?? undefined}
                  format={metricFormat(m.metric)}
                  helpText={SCORECARD_METRIC_HELP[m.metric]}
                  compact
                  windowLabel="Last 30d"
                  note={null}
                />
              );
            })}
          </div>
        )}
      </div>

      <div className="card">
        <h3 style={{ marginBottom: 12, fontSize: '0.875rem', fontWeight: 664, color: 'var(--loop-neutral-900)' }}>PRs</h3>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
          <label style={{ color: 'var(--loop-neutral-600)', fontSize: '0.75rem' }}>Verdict:</label>
          <select value={verdictFilter} onChange={(e) => changeVerdict(e.target.value)} style={{ ...inputStyle, minWidth: 160 }}>
            <option value="">All</option>
            <option value="APPROVE">APPROVE</option>
            <option value="NEEDS_HUMAN">NEEDS_HUMAN</option>
            <option value="REQUEST_CHANGES">REQUEST_CHANGES</option>
            <option value="TOO_LARGE">TOO_LARGE</option>
          </select>
        </div>

        {isLoading && <p style={{ color: 'var(--loop-neutral-700)', fontSize: '0.875rem' }}>Loading…</p>}
        {error && <p className="error">Error: {error instanceof Error ? error.message : String(error)}</p>}

        {!isLoading && !error && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ minWidth: '100%', fontSize: '0.875rem', borderCollapse: 'collapse' }}>
              <thead style={{ textAlign: 'left', background: 'var(--loop-neutral-200)' }}>
                <tr>
                  {['PR', 'Title', 'Author', 'Branch', 'Verdict', 'Cost', 'Duration', 'When'].map(h => (
                    <th key={h} style={{ padding: '8px 16px', color: 'var(--loop-neutral-700)', fontWeight: 528 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {allRows.length === 0 && (
                  <tr>
                    <td colSpan={8} style={{ padding: '24px 16px', textAlign: 'center', color: 'var(--loop-neutral-500)' }}>
                      No PRs found.
                    </td>
                  </tr>
                )}
                {allRows.map(p => (
                  <tr key={p.pr} style={{ borderTop: '1px solid var(--loop-neutral-200)' }}>
                    <td style={{ padding: '8px 16px' }}>
                      <Link to={`/repos/${org}/${repo}/pr/${p.pr}`} style={{ color: 'var(--loop-blue-600)' }}>
                        #{p.pr}
                      </Link>
                    </td>
                    <td style={{ padding: '8px 16px' }}>{p.title ?? '—'}</td>
                    <td style={{ padding: '8px 16px' }}>{p.author ?? '—'}</td>
                    <td style={{ padding: '8px 16px', fontFamily: 'monospace', fontSize: '0.75rem' }}>{p.branch ?? '—'}</td>
                    <td style={{ padding: '8px 16px' }}>{p.verdict_state}</td>
                    <td style={{ padding: '8px 16px' }}>{fmtCost(p.cost_usd)}</td>
                    <td style={{ padding: '8px 16px' }}>{fmtDuration(p.duration_ms)}</td>
                    <td style={{ padding: '8px 16px', color: 'var(--loop-neutral-600)' }}>{fmtDate(p.ts)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {data?.next_cursor && !isLoading && (
          <div style={{ marginTop: 12 }}>
            <button onClick={loadMore} className="btn-secondary">Load more</button>
          </div>
        )}
      </div>
    </div>
  );
}
